import React, { useEffect, useState } from 'react';
import { Save, Loader2, Type, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useSiteTexts, pushTexts } from '../../hooks/useSiteText';

const inp = 'w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-pink-brand';
const Card = ({ children }) => <div className="glass-card p-5">{children}</div>;

// Etichette user-friendly per ogni chiave (raggruppate per pagina)
const TEXT_GROUPS = [
  {
    title: 'Header (logo)',
    items: [
      ['header.logo.line1', 'Riga 1 (es. "A.S.D.P.")'],
      ['header.logo.line2', 'Riga 2 — prima parola (es. "Primavera")'],
      ['header.logo.line3', 'Riga 3 — seconda parola (es. "Cavese")'],
    ],
  },
  {
    title: 'Hero (homepage)',
    items: [
      ['hero.location', 'Località mostrata sotto la data'],
      ['hero.subtitle_prefix', 'Prefisso del sottotitolo (es. "Memorial")'],
      ['hero.btn_calendario', 'Pulsante Calendario'],
      ['hero.btn_squadre', 'Pulsante Squadre'],
      ['hero.btn_brochure', 'Pulsante Brochure'],
    ],
  },
  {
    title: 'Sezioni Homepage',
    items: [
      ['home.partners_title', 'Titolo sezione Partners'],
      ['home.partners_subtitle', 'Sottotitolo sezione Partners'],
      ['home.banner_iscrizioni', 'Banner iscrizioni'],
      ['home.banner_cta', 'CTA banner'],
    ],
  },
  {
    title: 'Pagina "Chi Siamo"',
    items: [
      ['chisiamo.title', 'Titolo pagina'],
      ['chisiamo.missione_title', 'Titolo "La Nostra Missione"'],
      ['chisiamo.comitato_title', 'Titolo "Comitato Organizzatore"'],
      ['chisiamo.ringraziamenti_title', 'Titolo Ringraziamenti'],
    ],
  },
  {
    title: 'Pagina "Squadre"',
    items: [
      ['squadre.title', 'Titolo'],
      ['squadre.subtitle', 'Sottotitolo'],
    ],
  },
  {
    title: 'Pagina "Calendario"',
    items: [
      ['calendario.title', 'Titolo'],
      ['calendario.subtitle', 'Sottotitolo'],
    ],
  },
  {
    title: 'Pagina "Classifiche & Risultati"',
    items: [
      ['classifiche.title', 'Titolo'],
      ['classifiche.subtitle', 'Sottotitolo'],
    ],
  },
  {
    title: 'Pagina "Regolamento"',
    items: [
      ['regolamento.title', 'Titolo'],
      ['regolamento.subtitle', 'Sottotitolo'],
    ],
  },
  {
    title: 'Pagina "Edizioni Passate"',
    items: [
      ['edizioni.title', 'Titolo'],
      ['edizioni.subtitle', 'Sottotitolo'],
      ['edizioni.dettaglio_btn', 'Pulsante dettaglio edizione'],
    ],
  },
  {
    title: 'Pagina "Partners"',
    items: [
      ['partners.title', 'Titolo'],
      ['partners.subtitle', 'Sottotitolo'],
    ],
  },
  {
    title: 'Pagina "Galleria"',
    items: [
      ['gallery.title', 'Titolo'],
      ['gallery.subtitle', 'Sottotitolo'],
    ],
  },
  {
    title: 'Pagina "Video"',
    items: [
      ['video.title', 'Titolo'],
      ['video.subtitle', 'Sottotitolo'],
    ],
  },
  {
    title: 'Pagina "Contatti"',
    items: [
      ['contatti.title', 'Titolo'],
      ['contatti.subtitle', 'Sottotitolo'],
      ['contatti.form_title', 'Titolo form'],
      ['contatti.form_nome', 'Etichetta campo Nome'],
      ['contatti.form_email', 'Etichetta campo Email'],
      ['contatti.form_messaggio', 'Etichetta campo Messaggio'],
      ['contatti.form_submit', 'Pulsante invio'],
      ['contatti.form_thanks', 'Messaggio di ringraziamento'],
    ],
  },
  {
    title: 'Footer',
    items: [
      ['footer.description', 'Descrizione (sotto al logo)'],
      ['footer.contatti_title', 'Titolo colonna Contatti'],
      ['footer.linkutili_title', 'Titolo colonna Link Utili'],
      ['footer.info_label', 'Etichetta "Info:" prima dei numeri'],
      ['footer.copyright', 'Testo copyright (no anno)'],
      ['footer.area_riservata', 'Link "Area Riservata"'],
      ['footer.hashtag', 'Hashtag in fondo'],
    ],
  },
];

export default function TextsEditor() {
  const { map, refresh } = useSiteTexts();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!draft && map) setDraft({ ...map });
  }, [map, draft]);

  if (!draft) return <div className="text-center py-10"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const filteredGroups = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return TEXT_GROUPS;
    return TEXT_GROUPS
      .map((g) => ({ ...g, items: g.items.filter(([k, lblTxt]) => k.includes(q) || lblTxt.toLowerCase().includes(q) || (draft[k] || '').toLowerCase().includes(q)) }))
      .filter((g) => g.items.length > 0);
  })();

  const save = async () => {
    setSaving(true);
    try {
      await api.updateSettings({ site_texts_json: JSON.stringify(draft) });
      pushTexts(draft);
      toast.success('Testi del sito aggiornati');
      refresh();
    } catch (e) {
      toast.error(e?.message || 'Errore di salvataggio');
    }
    setSaving(false);
  };

  const resetField = (k) => {
    // Rimuovi la chiave: il sito userà il fallback nel codice
    const next = { ...draft }; delete next[k]; setDraft(next);
  };

  return (
    <div className="space-y-4" data-testid="texts-editor">
      <Card>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Type size={18} className="text-pink-brand" />
            <h2 className="text-white font-bold text-lg">Testi del sito</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca…" className={inp + ' pl-9 w-64'} data-testid="texts-search" />
            </div>
            <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-5 !text-sm" data-testid="texts-save">
              <Save size={14} /> {saving ? 'Salvo…' : 'Salva tutto'}
            </button>
          </div>
        </div>
        <p className="text-white/60 text-xs mt-3">
          Modifica qui ogni testo visibile sul sito (intestazione, footer, titoli pagine, pulsanti, hashtag).
          Lascia il campo vuoto e premi <span className="text-pink-brand">RESET</span> per ripristinare il testo originale.
        </p>
      </Card>

      {filteredGroups.map((group) => (
        <Card key={group.title}>
          <h3 className="text-pink-brand font-bold text-sm uppercase tracking-widest mb-3">{group.title}</h3>
          <div className="space-y-2">
            {group.items.map(([key, label]) => (
              <div key={key} className="grid sm:grid-cols-12 gap-2 items-center" data-testid={`text-row-${key}`}>
                <div className="sm:col-span-4">
                  <div className="text-white text-xs font-semibold">{label}</div>
                  <code className="text-white/40 text-[10px]">{key}</code>
                </div>
                <input value={draft[key] ?? ''} onChange={(e) => set(key, e.target.value)} className={inp + ' sm:col-span-7'} />
                <button onClick={() => resetField(key)} className="text-white/60 hover:text-pink-brand text-[10px] font-bold uppercase tracking-widest sm:col-span-1 inline-flex items-center gap-1"><RotateCcw size={10} /> Reset</button>
              </div>
            ))}
          </div>
        </Card>
      ))}

      <button onClick={save} disabled={saving} className="btn-primary"><Save size={14} /> {saving ? 'Salvo…' : 'Salva tutto'}</button>
    </div>
  );
}
